import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import NavigationMenu from '@/components/shared/Menu';
import { ThemeToggle } from '@/components/shared';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';

interface WhoopConnectionStatus {
    connected: boolean;
    token_valid: boolean;
    token_expires: string | null;
    last_sync: string | null;
}

/**
 * Simplified Whoop OAuth component for distributed AWS environments.
 * Simple OAuth 2.0 authorization code flow without PKCE.
 */
const WhoopSimple: React.FC = () => {
    const [status, setStatus] = useState<WhoopConnectionStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userInfo, setUserInfo] = useState<any>(null);
    const navigate = useNavigate();
    const { isDark } = useTheme();

    useEffect(() => {
        // Get user info
        const storedUserInfo = localStorage.getItem('user_info');
        if (storedUserInfo) {
            try {
                setUserInfo(JSON.parse(storedUserInfo));
            } catch (e) {
                console.error('Error parsing user info:', e);
            }
        }

        // Check connection status
        checkConnectionStatus();
    }, []);

    const checkConnectionStatus = async () => {
        try {
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch(getApiUrl('/api/users/whoopstatus/'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await safeJsonParse<WhoopConnectionStatus>(response, 'checking Whoop status');
            
            if (result.success) {
                setStatus(result.data!);
            } else {
                console.error('Failed to check status:', result.error);
                setStatus({
                    connected: false,
                    token_valid: false,
                    token_expires: null,
                    last_sync: null
                });
            }
        } catch (error) {
            console.error('Error checking connection status:', error);
            setError('Unable to check connection status');
        }
    };

    const initiateOAuth = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                setError('Please log in again');
                navigate('/login');
                return;
            }

            // Get OAuth initiation parameters using existing endpoint
            const response = await fetch(getApiUrl('/api/whoop/auth/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({})
            });

            const result = await safeJsonParse<{ state: string; code_verifier: string; auth_url: string }>(response, 'initiating Whoop OAuth');
            
            if (!result.success) {
                throw new Error(result.error || 'Failed to initiate OAuth');
            }
            
            // Store OAuth parameters in localStorage for callback
            localStorage.setItem('whoop_oauth_state', result.data!.state);
            localStorage.setItem('whoop_code_verifier', result.data!.code_verifier);

            // Redirect to Whoop authorization
            window.location.href = result.data!.auth_url;

        } catch (error: any) {
            console.error('OAuth initiation failed:', error);
            setError(error.message || 'Failed to initiate Whoop connection');
        } finally {
            setLoading(false);
        }
    };

    const disconnect = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('access');
            const response = await fetch(getApiUrl('/api/whoop/disconnect/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                setSuccess('Whoop account disconnected successfully!');
                await checkConnectionStatus();
                
                // Clean up OAuth state
                localStorage.removeItem('whoop_oauth_state');
                localStorage.removeItem('whoop_code_verifier');
            } else {
                throw new Error('Failed to disconnect');
            }
        } catch (error: any) {
            setError(error.message || 'Failed to disconnect Whoop account');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
            {/* Navigation Menu & Theme Toggle - Fixed Position */}
            <div className="fixed top-4 left-4 z-50">
                <NavigationMenu userInfo={userInfo} isDark={isDark} showNavigation={true} />
            </div>

            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="max-w-2xl w-full space-y-6">
                {/* Header */}
                <div className="text-center">
                    <img 
                        src={isDark ? "/logos/whoop/Logo/Wordmark/WHOOP Logo White.svg" : "/logos/whoop/Logo/Wordmark/WHOOP Logo Black.svg"}
                        alt="WHOOP Logo"
                        className="mx-auto mb-8"
                        style={{ minWidth: 100, maxWidth: 200 }}
                    />
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Connect Your Whoop Account (Simplified)
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Streamlined OAuth 2.0 connection for distributed AWS environments
                    </p>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
                        {error}
                    </div>
                )}

                {/* Success Display */}
                {success && (
                    <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">
                        {success}
                    </div>
                )}

                {/* Connection Status */}
                <div className="cgm-card p-6 mb-4">
                    <div className="flex items-center gap-4 mb-4">
                        <h3 className="text-lg font-medium cgm-text-primary">Connection Status</h3>
                        <span className={`px-3 py-1 rounded-full text-sm ${
                            status?.connected 
                                ? status?.token_valid 
                                    ? 'cgm-alert-success' 
                                    : 'cgm-alert-warning'
                                : 'cgm-alert-error'
                        }`}>
                            {status?.connected 
                                ? status?.token_valid 
                                    ? 'Connected' 
                                    : 'Token Expired'
                                : 'Not Connected'
                            }
                        </span>
                    </div>

                    {status?.last_sync && (
                        <p className="text-sm cgm-text-muted mb-4">
                            Last sync: {new Date(status.last_sync).toLocaleString()}
                        </p>
                    )}

                    <div className="flex gap-3 justify-center flex-wrap">
                        {status?.connected ? (
                            <>
                                <button
                                    onClick={disconnect}
                                    disabled={loading}
                                    className="cgm-button px-4 py-2"
                                >
                                    {loading ? 'Disconnecting...' : 'Disconnect'}
                                </button>
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="cgm-button px-4 py-2"
                                >
                                    Go to Dashboard
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={initiateOAuth}
                                    disabled={loading}
                                    className="cgm-button px-4 py-2"
                                >
                                    {loading ? 'Connecting...' : 'Connect with Whoop (Simple)'}
                                </button>
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="cgm-button px-4 py-2"
                                >
                                    Go to Dashboard
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Info Card */}
                <div className="cgm-card p-6">
                    <h3 className="text-lg font-medium cgm-text-primary mb-3">Simplified OAuth Flow</h3>
                    <p className="text-sm cgm-text-secondary mb-3">
                        This implementation is optimized for distributed AWS environments:
                    </p>
                    <ul className="text-sm cgm-text-secondary space-y-1">
                        <li>• No server-side state management</li>
                        <li>• Simple OAuth 2.0 flow</li>
                        <li>• Works with CloudFront → ALB → Django</li>
                        <li>• Simplified error handling</li>
                        <li>• Better timeout management</li>
                    </ul>
                </div>

                {/* Security Notice */}
                <div className="cgm-card rounded-lg p-4">
                    <p className="text-sm cgm-text-secondary">
                        <strong>Security:</strong> This simplified flow maintains the same security standards.
                        Your Whoop credentials are never stored. We only store secure OAuth tokens that can be revoked.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WhoopSimple;
